import React, { useState, useEffect } from 'react';
import {
  FaFileInvoice,
  FaSearch,
  FaFilter,
  FaEye,
  FaDownload,
  FaCheckCircle,
  FaTimes,
  FaClock,
  FaDollarSign,
  FaBuilding,
  FaEnvelope,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaExclamationTriangle
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

const VendorInvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [activeTab, setActiveTab] = useState('pdf');

  // Reporting Totals — gstPaidTotal feeds the BAS 1B claim (GST credits).
  // Counts paid-only because we don't claim credit on unpaid invoices.
  const [reportStats, setReportStats] = useState({
    totalPending: 0,
    totalPaid: 0,
    countPending: 0,
    countPaid: 0,
    gstPaidTotal: 0,
    gstMissingCount: 0,
  });

  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    // Calculate totals whenever invoices change.
    const stats = invoices.reduce((acc, inv) => {
      if (inv.status === 'PENDING' || inv.status === 'PROCESSING') {
        acc.totalPending += (inv.amount || 0);
        acc.countPending++;
      } else if (inv.status === 'PAID') {
        acc.totalPaid += (inv.amount || 0);
        acc.countPaid++;
        // GST paid (BAS 1B). Only counts PAID invoices that have tax populated.
        // Invoices missing tax can't contribute to 1B and we surface a count
        // so admin sees how much is leaking.
        const taxNum = parseFloat(inv.tax) || 0;
        if (taxNum > 0) {
          acc.gstPaidTotal += taxNum;
        } else {
          acc.gstMissingCount++;
        }
      }
      return acc;
    }, { totalPending: 0, totalPaid: 0, countPending: 0, countPaid: 0, gstPaidTotal: 0, gstMissingCount: 0 });
    setReportStats(stats);
  }, [invoices]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.VENDOR_INVOICE_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'listInvoices',
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 1000
        })
      });

      const data = await response.json();

      if (data.success) {
        // Sort by date (newest first)
        const sorted = (data.invoices || []).sort((a, b) => {
          const dateA = a.invoiceDate || a.createdAt || 0;
          const dateB = b.invoiceDate || b.createdAt || 0;
          return dateB - dateA;
        });
        setInvoices(sorted);
      } else {
        setError(data.error || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAmount = async () => {
    if (!selectedInvoice) return;

    // GST consistency check — if both subtotal and tax are populated, their
    // sum should equal amount (with rounding tolerance). Surfaces typos.
    const amt = parseFloat(selectedInvoice.amount) || 0;
    const sub = parseFloat(selectedInvoice.subtotal) || 0;
    const tax = parseFloat(selectedInvoice.tax) || 0;
    if (sub > 0 && tax > 0) {
      const diff = Math.abs(amt - (sub + tax));
      if (diff > 0.05) {
        if (!confirm(
            `Subtotal (${sub.toFixed(2)}) + Tax (${tax.toFixed(2)}) = ${(sub + tax).toFixed(2)}\n` +
            `but Total amount = ${amt.toFixed(2)} (off by $${diff.toFixed(2)}).\n\n` +
            `Save anyway?`
        )) {
          return;
        }
      }
    }

    try {
      setProcessing(true);
      const response = await fetch(API_ENDPOINTS.VENDOR_INVOICE_MANAGEMENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'updateInvoice',
          invoiceId: selectedInvoice.invoiceId,
          amount: parseFloat(selectedInvoice.amount),
          // Subtotal (ex-GST) and tax (GST). Backend already supports these
          // fields per lambda-vendor-invoice-management.js:256-258. Without
          // them captured the BAS 1B credit can't be claimed.
          subtotal: selectedInvoice.subtotal !== undefined && selectedInvoice.subtotal !== '' ? parseFloat(selectedInvoice.subtotal) : undefined,
          tax: selectedInvoice.tax !== undefined && selectedInvoice.tax !== '' ? parseFloat(selectedInvoice.tax) : undefined,
          vendorName: selectedInvoice.vendorName,
          vendorEmail: selectedInvoice.vendorEmail,
          vendorBSB: selectedInvoice.vendorBSB,
          vendorAccount: selectedInvoice.vendorAccount,
          vendorBusinessName: selectedInvoice.vendorBusinessName,
          vendorCRN: selectedInvoice.vendorCRN,
          vendorABN: selectedInvoice.vendorABN,
          emailCC: selectedInvoice.emailCC,
          invoiceNumber: selectedInvoice.invoiceNumber,
          dueDate: selectedInvoice.dueDate
        })
      });

      const data = await response.json();
      if (data.success) {
        setSelectedInvoice(data.invoice); // Update local state
        setIsEditingAmount(false);
        loadInvoices(); // Refresh list background
      } else {
        setError(data.error || 'Failed to update invoice details');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError('Failed to update invoice details');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    // Handle both epoch seconds and milliseconds
    let date;
    if (typeof dateValue === 'number') {
      // If > 10 billion, assume milliseconds (Date.now()), otherwise seconds (Unix timestamp)
      date = dateValue > 10000000000 ? new Date(dateValue) : new Date(dateValue * 1000);
    } else {
      date = new Date(dateValue);
    }

    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-xs font-semibold">Pending</span>,
      APPROVED: <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs font-semibold">Approved</span>,
      REJECTED: <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs font-semibold">Rejected</span>,
      PAID: <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs font-semibold">Paid</span>,
      OVERDUE: <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs font-semibold">Overdue</span>,
      PROCESSING: <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs font-semibold">Processing</span>
    };
    return badges[status] || badges.PENDING;
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchQuery ||
      invoice.invoiceId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vendorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.vendorEmail?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  const handleViewInvoice = async (invoice) => {
    try {
      setError(null);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.VENDOR_INVOICE_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'getInvoice',
          invoiceId: invoice.invoiceId
        })
      });

      const data = await response.json();

      if (data.success) {
        setSelectedInvoice(data.invoice);
        setIsEditingAmount(false); // Reset edit state
        setShowViewModal(true);
      } else {
        setError(data.error || 'Failed to load invoice details');
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice details');
    }
  };

  const handleApprove = async () => {
    if (!selectedInvoice) return;

    // Pre-validation: Don't allow paying $0 invoices
    if (!selectedInvoice.amount || parseFloat(selectedInvoice.amount) <= 0) {
      setError('Cannot approve an invoice with $0.00 amount. Please update the amount first.');
      setShowApproveModal(false);
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      // Step 1: Approve the invoice in DynamoDB
      const approveResponse = await fetch(API_ENDPOINTS.VENDOR_INVOICE_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'approveInvoice',
          invoiceId: selectedInvoice.invoiceId,
          approvedBy: 'admin@peaknet.com.au'
        })
      });

      const approveData = await approveResponse.json();
      if (!approveData.success) {
        throw new Error(approveData.error || 'Failed to approve invoice');
      }

      // Refresh list to show 'APPROVED' status in background
      await loadInvoices();

      // Step 2: Trigger the payment via Stripe
      const paymentResponse = await fetch(API_ENDPOINTS.VENDOR_INVOICE_PAYMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'payInvoice',
          invoiceId: selectedInvoice.invoiceId
        })
      });

      const paymentData = await paymentResponse.json();
      if (!paymentData.success) {
        console.error('Payment failure:', paymentData.error);
        setError('APPROVAL SUCCESSFUL, but Stripe Payment failed: ' + paymentData.error);
        // DO NOT close modal or clear selection so user can see error and fix it
        setShowApproveModal(false); // Close the confirmation but keep the View modal open
        return;
      }

      // Full success
      setShowApproveModal(false);
      setShowViewModal(false);
      setSelectedInvoice(null);
      await loadInvoices();

    } catch (err) {
      console.error('Error in approval/payment workflow:', err);
      setError(err.message || 'Failed to complete approval process');
      setShowApproveModal(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedInvoice || !rejectReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.VENDOR_INVOICE_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'rejectInvoice',
          invoiceId: selectedInvoice.invoiceId,
          rejectedBy: 'admin@peaknet.com.au', // TODO: Get from auth context
          rejectedReason: rejectReason
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowRejectModal(false);
        setShowViewModal(false);
        setSelectedInvoice(null);
        setRejectReason('');
        await loadInvoices();
      } else {
        setError(data.error || 'Failed to reject invoice');
      }
    } catch (err) {
      console.error('Error rejecting invoice:', err);
      setError('Failed to reject invoice');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPDF = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">Vendor Invoices</h1>
          <p className="text-[#a89984]">Manage, review, and pay vendor invoices</p>
        </div>
        {/* Simple Report Stats */}
        <div className="flex gap-4">
          <div className="bg-[#1d2021] p-3 rounded border border-[#504945] min-w-[150px]">
            <div className="text-[#a89984] text-xs font-bold uppercase">Pending Payables</div>
            <div className="text-[#FFD700] text-xl font-bold">{formatCurrency(reportStats.totalPending)}</div>
            <div className="text-[#a89984] text-xs">{reportStats.countPending} invoices</div>
          </div>
          <div className="bg-[#1d2021] p-3 rounded border border-[#504945] min-w-[150px]">
            <div className="text-[#a89984] text-xs font-bold uppercase">Paid (MTD)</div>
            <div className="text-green-400 text-xl font-bold">{formatCurrency(reportStats.totalPaid)}</div>
            <div className="text-[#a89984] text-xs">{reportStats.countPaid} invoices</div>
          </div>
          {/* GST Paid total — directly feeds the BAS 1B claim. Counts ONLY
              paid invoices with tax populated; warns when any are missing. */}
          <div className="bg-[#1d2021] p-3 rounded border border-[#FFD700] min-w-[150px]">
            <div className="text-[#FFD700] text-xs font-bold uppercase">GST Paid · BAS 1B</div>
            <div className="text-[#FFD700] text-xl font-bold">{formatCurrency(reportStats.gstPaidTotal)}</div>
            <div className="text-[#a89984] text-xs">
              {reportStats.gstMissingCount > 0
                ? <span className="text-red-400">⚠ {reportStats.gstMissingCount} paid w/o GST</span>
                : 'all paid invoices captured'}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#282828] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a89984]" />
            <input
              type="text"
              placeholder="Search by invoice ID, number, vendor name, or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] placeholder-[#a89984] focus:outline-none focus:border-[#FFD700]"
            />
          </div>
        </div>
        <div className="md:w-48">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
          >
            <option value="all">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="PAID">Paid</option>
            <option value="OVERDUE">Overdue</option>
            <option value="PROCESSING">Processing</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-[#282828] rounded-lg overflow-hidden border border-[#504945]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1d2021] border-b border-[#504945]">
              <tr>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Invoice ID</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Vendor</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Date</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Amount</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">GST</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#504945]">
              {paginatedInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-[#a89984]">
                    {searchQuery || statusFilter !== 'all' ? 'No invoices found matching your filters' : 'No invoices found'}
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice) => (
                  <tr key={invoice.invoiceId} className="hover:bg-[#504945] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-medium">{invoice.invoiceNumber || invoice.invoiceId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#ebdbb2] font-medium">{invoice.vendorName || 'N/A'}</div>
                      <div className="text-[#a89984] text-sm">{invoice.vendorEmail || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2]">{formatDate(invoice.invoiceDate || invoice.createdAt)}</div>
                      {invoice.dueDate && (
                        <div className="text-[#a89984] text-xs">Due: {formatDate(invoice.dueDate)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-semibold">{formatCurrency(invoice.amount)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.tax && parseFloat(invoice.tax) > 0
                        ? <div className="text-[#FFD700] font-medium">{formatCurrency(invoice.tax)}</div>
                        : invoice.status === 'PAID'
                          ? <div className="text-red-400 text-xs" title="Paid but no GST captured — won't contribute to BAS 1B">⚠ missing</div>
                          : <div className="text-[#928374] text-xs">—</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewInvoice(invoice)}
                        className="p-2 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30 transition-colors"
                        title="View & Edit Invoice"
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-[#282828] px-6 py-4 flex items-center justify-between border-t border-[#504945]">
            <div className="text-[#a89984] text-sm">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length} invoices
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#504945] transition-colors"
              >
                <FaChevronLeft />
              </button>
              <span className="text-[#ebdbb2] px-4">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#504945] transition-colors"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View/Edit Invoice Modal - Split Layout */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-lg w-full max-w-7xl h-[90vh] flex flex-col border border-[#504945]">

            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-[#504945] bg-[#1d2021]">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-[#ebdbb2]">Invoice: {selectedInvoice.invoiceId}</h2>
                {getStatusBadge(selectedInvoice.status)}
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedInvoice(null);
                  setIsEditingAmount(false);
                }}
                className="text-[#a89984] hover:text-[#ebdbb2] p-2"
              >
                <FaTimes size={24} />
              </button>
            </div>

            {/* Split Content */}
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT COLUMN: PDF / Email View (60%) */}
              <div className="w-[60%] border-r border-[#504945] bg-[#32302f] flex flex-col">
                {/* Tabs */}
                <div className="flex border-b border-[#504945] bg-[#1d2021]">
                  <button
                    onClick={() => setActiveTab('pdf')}
                    className={`px-6 py-3 text-sm font-medium ${activeTab === 'pdf' ? 'bg-[#32302f] text-[#ebdbb2] border-t-2 border-[#FFD700]' : 'text-[#a89984] hover:text-[#ebdbb2]'}`}
                  >
                    <FaFileInvoice className="inline mr-2" /> PDF Document
                  </button>
                  <button
                    onClick={() => setActiveTab('email')}
                    className={`px-6 py-3 text-sm font-medium ${activeTab === 'email' ? 'bg-[#32302f] text-[#ebdbb2] border-t-2 border-[#FFD700]' : 'text-[#a89984] hover:text-[#ebdbb2]'}`}
                  >
                    <FaEnvelope className="inline mr-2" /> Original Email
                  </button>
                </div>

                {/* Content Viewer */}
                <div className="flex-1 bg-[#504945] relative overflow-hidden">
                  {activeTab === 'pdf' ? (
                    selectedInvoice.pdfUrl ? (
                      <>
                        <div className="absolute top-2 right-2 z-10">
                          <button
                            onClick={() => window.open(selectedInvoice.pdfUrl, '_blank')}
                            className="bg-[#1d2021] text-[#ebdbb2] px-3 py-1 rounded shadow hover:bg-black text-xs flex items-center gap-1"
                          >
                            <FaDownload /> Open External
                          </button>
                        </div>
                        <iframe
                          src={selectedInvoice.pdfUrl}
                          className="w-full h-full"
                          title="Invoice PDF"
                        />
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-[#a89984]">
                        <FaExclamationTriangle size={48} className="mb-4" />
                        <p>No PDF Preview Available</p>
                      </div>
                    )
                  ) : (
                    <div className="w-full h-full bg-white text-black p-8 overflow-y-auto">
                      <div className="mb-6 border-b pb-4">
                        <p className="font-bold text-gray-900">From: <span className="font-normal text-gray-700">{selectedInvoice.vendorEmail}</span></p>
                        <p className="font-bold text-gray-900">Subject: <span className="font-normal text-gray-700">{selectedInvoice.emailSubject || 'Invoice Received'}</span></p>
                        <p className="font-bold text-gray-900">Date: <span className="font-normal text-gray-700">{new Date(selectedInvoice.createdAt).toLocaleString()}</span></p>
                      </div>
                      <div className="prose max-w-none text-gray-800">
                        {selectedInvoice.emailBody ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedInvoice.emailBody }} />
                        ) : (
                          <p className="text-gray-500 italic">No email body content captured for this invoice.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN: Data Entry & Action (40%) */}
              <div className="w-[40%] bg-[#282828] flex flex-col overflow-y-auto">
                <div className="p-6 space-y-6">

                  {/* Vendor Details Section */}
                  <div className="bg-[#1d2021] p-4 rounded border border-[#504945]">
                    <h3 className="text-[#a89984] text-xs font-bold uppercase mb-3 text-white">Vendor Details</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-[#a89984] text-xs mb-1">Vendor Name</label>
                        <input
                          type="text"
                          value={selectedInvoice.vendorName || ''}
                          onChange={(e) => {
                            setSelectedInvoice({ ...selectedInvoice, vendorName: e.target.value });
                            setIsEditingAmount(true);
                          }}
                          className="w-full bg-[#32302f] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[#a89984] text-xs mb-1">Vendor Email (Multiple separated by comma)</label>
                        <input
                          type="text"
                          value={selectedInvoice.vendorEmail || ''}
                          onChange={(e) => {
                            setSelectedInvoice({ ...selectedInvoice, vendorEmail: e.target.value });
                            setIsEditingAmount(true);
                          }}
                          className="w-full bg-[#32302f] text-[#ebdbb2] border border-[#504945] rounded p-2 outline-none focus:border-[#FFD700]"
                        />
                      </div>
                      <div>
                        <label className="block text-[#a89984] text-xs mb-1">Email CC</label>
                        <input
                          type="text"
                          value={selectedInvoice.emailCC || ''}
                          onChange={(e) => {
                            setSelectedInvoice({ ...selectedInvoice, emailCC: e.target.value });
                            setIsEditingAmount(true);
                          }}
                          placeholder="cc@example.com, another@example.com"
                          className="w-full bg-[#32302f] text-[#ebdbb2] border border-[#504945] rounded p-2 outline-none focus:border-[#FFD700]"
                        />
                      </div>

                      {/* Payment Destination */}
                      <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-[#504945]">
                        <div className="col-span-2 text-[#a89984] text-xs font-bold mb-1">Payment Destination</div>
                        <div className="col-span-2">
                          <label className="block text-[#a89984] text-xs mb-1">Business Name (Account Name)</label>
                          <input
                            type="text"
                            value={selectedInvoice.vendorBusinessName || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, vendorBusinessName: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[#a89984] text-xs mb-1">BSB</label>
                          <input
                            type="text"
                            placeholder="000-000"
                            value={selectedInvoice.vendorBSB || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, vendorBSB: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[#a89984] text-xs mb-1">Account Number</label>
                          <input
                            type="text"
                            placeholder="00000000"
                            value={selectedInvoice.vendorAccount || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, vendorAccount: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[#a89984] text-xs mb-1">Customer Ref Number (CRN)</label>
                          <input
                            type="text"
                            value={selectedInvoice.vendorCRN || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, vendorCRN: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info Section */}
                  <div className="bg-[#1d2021] p-4 rounded border border-[#504945] relative">
                    <h3 className="text-[#a89984] text-xs font-bold uppercase mb-3 text-white">Payment Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-[#a89984] text-xs mb-1">Invoice Number *</label>
                        <input
                          type="text"
                          value={selectedInvoice.invoiceNumber || ''}
                          onChange={(e) => {
                            setSelectedInvoice({ ...selectedInvoice, invoiceNumber: e.target.value });
                            setIsEditingAmount(true);
                          }}
                          placeholder="Enter Invoice #"
                          className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[#a89984] text-xs mb-1">Total Amount inc-GST (AUD) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={selectedInvoice.amount || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, amount: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            className="w-full pl-6 pr-2 py-2 bg-[#282828] text-white border border-[#504945] rounded focus:border-[#FFD700] outline-none font-bold text-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[#a89984] text-xs mb-1">Due Date</label>
                        <input
                          type="date"
                          value={selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate * 1000).toISOString().split('T')[0] : ''}
                          onChange={(e) => {
                            const date = new Date(e.target.value);
                            const epoch = Math.floor(date.getTime() / 1000);
                            setSelectedInvoice({ ...selectedInvoice, dueDate: epoch });
                            setIsEditingAmount(true);
                          }}
                          className="w-full bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded p-2 focus:border-[#FFD700] outline-none"
                        />
                      </div>
                    </div>

                    {/* GST breakdown — required for BAS 1B credit. ABN is also
                        needed to lawfully claim GST credit on invoices ≥ $75 ex-GST.
                        "Auto-split" button computes subtotal+tax from amount assuming
                        the whole invoice is taxable (1/11th GST). For mixed-GST or
                        GST-free invoices admin needs to enter manually. */}
                    <div className="mt-4 pt-4 border-t border-[#504945]">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-[#FFD700] text-xs font-bold uppercase">GST Breakdown — for BAS 1B credit</h4>
                        <button
                          type="button"
                          onClick={() => {
                            const total = parseFloat(selectedInvoice.amount) || 0;
                            if (total <= 0) { alert('Set Total Amount first'); return; }
                            const tax = Math.round((total / 11) * 100) / 100;
                            const subtotal = Math.round((total - tax) * 100) / 100;
                            setSelectedInvoice({ ...selectedInvoice, subtotal, tax });
                            setIsEditingAmount(true);
                          }}
                          className="text-xs text-[#83a598] hover:text-[#8ec07c] underline"
                          title="Compute subtotal + tax as if fully GST-taxable (tax = amount / 11)"
                        >
                          Auto-split (10% GST)
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[#a89984] text-xs mb-1">Subtotal ex-GST</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedInvoice.subtotal ?? ''}
                              onChange={(e) => {
                                setSelectedInvoice({ ...selectedInvoice, subtotal: e.target.value });
                                setIsEditingAmount(true);
                              }}
                              placeholder="0.00"
                              className="w-full pl-6 pr-2 py-2 bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded focus:border-[#FFD700] outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[#a89984] text-xs mb-1">GST (10%)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedInvoice.tax ?? ''}
                              onChange={(e) => {
                                setSelectedInvoice({ ...selectedInvoice, tax: e.target.value });
                                setIsEditingAmount(true);
                              }}
                              placeholder="0.00"
                              className="w-full pl-6 pr-2 py-2 bg-[#282828] text-[#FFD700] border border-[#504945] rounded focus:border-[#FFD700] outline-none font-medium"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[#a89984] text-xs mb-1">Vendor ABN</label>
                          <input
                            type="text"
                            value={selectedInvoice.vendorABN || ''}
                            onChange={(e) => {
                              setSelectedInvoice({ ...selectedInvoice, vendorABN: e.target.value });
                              setIsEditingAmount(true);
                            }}
                            placeholder="11 digits"
                            maxLength={14}
                            className="w-full px-2 py-2 bg-[#282828] text-[#ebdbb2] border border-[#504945] rounded focus:border-[#FFD700] outline-none font-mono text-sm"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-[#928374] mt-2">
                        Required for BAS 1B claim. ABN must match the supplier on the invoice — credits ≥ $75 are denied without a valid ABN.
                      </p>
                    </div>

                    {/* Manual Save Button */}
                    {isEditingAmount && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={handleUpdateAmount}
                          disabled={processing}
                          className="px-4 py-2 bg-[#FFD700] text-black font-bold rounded hover:bg-[#F0C600] text-sm"
                        >
                          {processing ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Actions Section */}
                  <div className="pt-4 border-t border-[#504945]">
                    <div className="flex justify-between items-center">
                      <span className="text-[#a89984] text-sm">Action Required:</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowRejectModal(true)}
                          disabled={processing}
                          className="px-4 py-2 bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 font-semibold text-sm"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => setShowApproveModal(true)}
                          disabled={processing || selectedInvoice.status === 'APPROVED'}
                          className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 font-bold shadow-lg"
                        >
                          Approve & Pay
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-lg max-w-md w-full border border-[#504945]">
            <div className="p-6">
              <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Confirm Payment</h3>
              <p className="text-[#a89984] mb-4">
                You are about to approve this invoice for payment.
              </p>
              <div className="bg-[#1d2021] p-3 rounded mb-6">
                <div className="flex justify-between mb-1">
                  <span className="text-[#a89984]">Pay To:</span>
                  <span className="text-[#ebdbb2]">{selectedInvoice.vendorName}</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span className="text-[#a89984]">Amount:</span>
                  <span className="text-[#FFD700]">{formatCurrency(selectedInvoice.amount)}</span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  disabled={processing}
                  className="px-4 py-2 bg-[#1d2021] border border-[#504945] text-[#ebdbb2] rounded hover:bg-[#504945] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="px-4 py-2 bg-green-500/20 text-green-300 rounded hover:bg-green-600 font-bold flex items-center space-x-2"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                  <span>Confirm Approval</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#282828] rounded-lg max-w-md w-full border border-[#504945]">
            <div className="p-6">
              <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Reject Invoice</h3>
              <div className="mb-4">
                <label className="block text-[#a89984] text-sm mb-2">Rejection Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] placeholder-[#a89984] focus:outline-none focus:border-[#FFD700] resize-none"
                  rows="4"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  disabled={processing}
                  className="px-4 py-2 bg-[#1d2021] border border-[#504945] text-[#ebdbb2] rounded hover:bg-[#504945] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectReason.trim()}
                  className="px-4 py-2 bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 flex items-center space-x-2 disabled:opacity-50"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaTimes />}
                  <span>Reject</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorInvoiceManagement;
